
import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import {
  onAnimationEnd,
  removeWillChangeOnAnimationEnd,
} from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details
 * @property {HTMLDivElement} menuDrawer
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    // Keep the native details state and CSS state synchronized.
    this.refs.details.addEventListener('toggle', this.#onDetailsToggle);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('keyup', this.#onKeyUp);
    this.refs.details.removeEventListener('toggle', this.#onDetailsToggle);
  }

  /**
   * Synchronize the drawer's native open attribute
   * with its CSS class.
   */
  #onDetailsToggle = () => {
    const details = this.refs.details;

    if (!details.open) {
      details.classList.remove('menu-open');
      details
        .querySelector('summary')
        ?.setAttribute('aria-expanded', 'false');

      this.refs.menuDrawer.classList.remove(
        'menu-drawer--has-submenu-opened'
      );

      removeTrapFocus();
    }
  };

  /**
   * Close the drawer when Escape is pressed.
   *
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Whether the main drawer is open.
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Find the closest details element.
   *
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) {
      return this.refs.details;
    }

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the drawer.
   *
   * Prevent the browser's default summary behavior
   * so that JavaScript owns the open/close state.
   *
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault();

    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Open the main drawer or a submenu.
   *
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    // Explicitly open the native details element.
    details.open = true;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);

    requestAnimationFrame(() => {
      if (!details.open) return;

      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add(
          'menu-drawer--has-submenu-opened'
        );
      }

      const drawer = details.querySelector(
        '.menu-drawer, .menu-drawer__submenu'
      );

      onAnimationEnd(
        drawer || details,
        () => {
          if (details.open) {
            trapFocus(details);
          }
        },
        { subtree: false }
      );
    });
  }

  /**
   * Go back from a submenu.
   *
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main drawer.
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the nearest open drawer or submenu.
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');

    details.classList.remove('menu-open');

    this.refs.menuDrawer.classList.remove(
      'menu-drawer--has-submenu-opened'
    );

    const drawer = details.querySelector(
      '.menu-drawer, .menu-drawer__submenu'
    );

    onAnimationEnd(
      drawer || details,
      () => {
        // Do not reset a drawer that has reopened.
        if (details.classList.contains('menu-open')) return;

        reset(details);

        if (details === this.refs.details) {
          removeTrapFocus();

          const openDetails = this.querySelectorAll(
            'details[open]:not(accordion-custom > details)'
          );

          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Clean up animated elements.
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll(
      '.menu-drawer__animated-element'
    );

    allAnimated.forEach((element) => {
      element.addEventListener(
        'animationend',
        removeWillChangeOnAnimationEnd
      );
    });
  }

  /**
   * Temporarily disable accordion animations
   * during the initial drawer opening.
   *
   * @param {HTMLDetailsElement} details
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll(
      'accordion-custom .details-content'
    );

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });

    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element.
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');

  element
    .querySelector('summary')
    ?.setAttribute('aria-expanded', 'false');
}
